import { onBeforeUnmount } from 'vue';
import Confetti from '@/Plugins/Confetti';

// Indices, not colours: both palettes contain '#ffffff' and a colour lookup takes the first
// match, which would hand the endgame white the platform white's cut.
const sharedPapers = [...Confetti.palettes.platform, ...Confetti.palettes.endgame];

export const confettiPalettes = {
    platform: Confetti.palettes.platform.map((paper, index) => index),
    endgame: Confetti.palettes.endgame.map((paper, index) => Confetti.palettes.platform.length + index),
};

// One instance for the whole page: separate ones each mount a full-viewport canvas with their
// own frame loop and their own pile, so their drifts interpenetrate instead of building one.
let sharedConfetti = null;
let sharedUsers = 0;

/**
 * Runs confetti over a stage.
 *
 * @param {Object} [options] Instance options. A `container` opts out of the shared stage, since
 *                           a mounted layer cannot move between parents. Without one the stage
 *                           is the viewport.
 */
export const useConfetti = (options) => {
    const isShared = !options || !options.container;
    let own = null;

    if (isShared) {
        sharedUsers++;
    }

    // Built on first use: constructing one mounts a canvas and starts observing the viewport.
    const instance = () => {
        if (!isShared) {
            own = own || new Confetti(options);

            return own;
        }

        sharedConfetti = sharedConfetti || new Confetti({ papers: sharedPapers, ...options });

        return sharedConfetti;
    };

    const current = () => isShared ? sharedConfetti : own;

    const burst = (preset, overrides) => instance().burst(preset, overrides);

    const stream = (preset, overrides) => instance().stream({ preset, ...overrides });

    /** One-shot callback for the first piece to rise past `y`, a fraction of the stage. */
    const onceAboveY = (y, callback) => instance().onceAboveY(y, callback);

    /** Stops emitting. Paper already in the air keeps falling unless `clear` is set. */
    const stop = (stopOptions) => {
        const confetti = current();

        if (confetti) {
            confetti.stop(stopOptions);
        }
    };

    // Releases this caller's hold; the shared stage goes only with the last of them.
    const release = () => {
        if (!isShared) {
            if (own) {
                own.destroy();
                own = null;
            }

            return;
        }

        sharedUsers = Math.max(0, sharedUsers - 1);

        if (!sharedUsers && sharedConfetti) {
            sharedConfetti.destroy();
            sharedConfetti = null;
        }
    };

    onBeforeUnmount(release);

    return {
        burst,
        stream,
        onceAboveY,
        stop,
        release,
    };
};
